import React from 'react';
import ReactDOM from 'react-dom/client';
import MilesWidget from './MilesWidget';
import type { WidgetConfig, ViewType } from './types';
import { setWidgetStorageSettings, WidgetStorageSettings } from './utils/storage';

let widgetRoot: ReturnType<typeof ReactDOM.createRoot> | null = null;
let widgetContainer: HTMLDivElement | null = null;

function destroyMilesWidget() {
  if (widgetRoot) {
    widgetRoot.unmount();
    widgetRoot = null;
  }
  if (widgetContainer && widgetContainer.parentNode) {
    widgetContainer.parentNode.removeChild(widgetContainer);
  }
  widgetContainer = null;
  (window as any).MilesWidgetInitialized = false;
}

// Initialize the widget
function initMilesWidget(overrideConfig: Partial<WidgetConfig> = {}) {
  // Prevent duplicate initialization
  if ((window as any).MilesWidgetInitialized) {
    console.warn('[Miles AI Widget] Widget already initialized');
    return;
  }

  // Find the script tag
  const scripts = document.getElementsByTagName('script');
  let scriptElement: HTMLScriptElement | null = null;

  for (let i = scripts.length - 1; i >= 0; i--) {
    const script = scripts[i];
    if (script.src && script.src.includes('miles-ai-widget')) {
      scriptElement = script;
      break;
    }
  }

  // Fallback: check for script with data-dealer-id
  if (!scriptElement) {
    scriptElement = document.querySelector('script[data-dealer-id]');
  }

  if (!scriptElement) {
    console.error('[Miles AI Widget] Could not find widget script tag');
    return;
  }

  // Extract configuration from script attributes + optional global/override config
  const globalConfig =
    (window as any).MilesAIWidgetConfig &&
      typeof (window as any).MilesAIWidgetConfig === 'object'
      ? (window as any).MilesAIWidgetConfig
      : {};

  const scriptConfig: Partial<WidgetConfig> = {
    dealerId: scriptElement.getAttribute('data-dealer-id') || undefined,
    dealerName: scriptElement.getAttribute('data-dealer-name') || undefined,
    apiBase: scriptElement.getAttribute('data-api') || undefined,
    webUrl: scriptElement.getAttribute('data-web-url') || undefined,
    assetsUrl: scriptElement.getAttribute('data-assets-url') || undefined,
    statusMessage: scriptElement.getAttribute('data-status-message') || undefined,
    floatingButtonType: scriptElement.getAttribute('data-floating-button-type') || undefined,
  };

  const mergedConfig: Partial<WidgetConfig> = {
    ...globalConfig,
    ...scriptConfig,
    ...overrideConfig,
  };

  const dealerId = mergedConfig.dealerId;
  const dealerName = mergedConfig.dealerName || 'our dealership';
  const apiBase = mergedConfig.apiBase || window.location.origin;
  const webUrl = mergedConfig.webUrl || window.location.origin;
  const assetsUrl = mergedConfig.assetsUrl || window.location.origin;
  const statusMessage = mergedConfig.statusMessage || 'Online';
  const floatingButtonType = mergedConfig.floatingButtonType;

  if (!dealerId) {
    if (!(window as any).MilesAIWidgetInit) {
      (window as any).MilesAIWidgetInit = (config: Partial<WidgetConfig>) => initMilesWidget(config);
    }
    console.error(
      '[Miles AI Widget] Missing required dealerId. Provide data-dealer-id or call window.MilesAIWidgetInit({ dealerId, dealerName, ... }).'
    );
    return;
  }

  const config: WidgetConfig = {
    dealerId,
    dealerName,
    apiBase: apiBase.replace(/\/$/, ''),
    webUrl: webUrl.replace(/\/$/, ''),
    assetsUrl: assetsUrl.replace(/\/$/, ''),
    statusMessage,
    floatingButtonType,
  };

  // Create container for widget
  widgetContainer = document.createElement('div');
  widgetContainer.id = 'miles-ai-widget-root';
  widgetContainer.style.cssText = 'position: fixed; z-index: 2147483646;';
  document.body.appendChild(widgetContainer);

  // Render the widget
  widgetRoot = ReactDOM.createRoot(widgetContainer);
  widgetRoot.render(
    <React.StrictMode>
      <MilesWidget config={config} />
    </React.StrictMode>
  );

  // Mark as initialized
  (window as any).MilesWidgetInitialized = true;
}

// Expose public API at module load time so it's accessible immediately,
// independent of widget initialization
(window as any).MilesAIWidget = {
  version: '1.0.0',
  open: () => {
    const event = new CustomEvent('miles-widget-toggle');
    window.dispatchEvent(event);
  },
  setView: (view: ViewType) => {
    const event = new CustomEvent('miles-widget-set-view', {
      detail: { view },
    });
    window.dispatchEvent(event);
  },
  showInventory: () => {
    const event = new CustomEvent('miles-widget-show-inventory');
    window.dispatchEvent(event);
  },
  setStorage: (settings: WidgetStorageSettings) => {
    setWidgetStorageSettings(settings);
  },
  destroy: () => {
    destroyMilesWidget();
  },
  uninitialize: () => {
    destroyMilesWidget();
  },
};

if (!(window as any).MilesAIWidgetInit) {
  (window as any).MilesAIWidgetInit = (config: Partial<WidgetConfig>) => initMilesWidget(config);
}

if (!(window as any).MilesAIWidgetSetStorage) {
  (window as any).MilesAIWidgetSetStorage = (settings: WidgetStorageSettings) =>
    setWidgetStorageSettings(settings);
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initMilesWidget());
} else {
  initMilesWidget();
}

export default MilesWidget;
